import {
  adminPlanCronSchema,
  adminPlanDurationsSchema,
  adminPlanRetryResultSchema,
  adminPlanRunDetailSchema,
  adminPlanRunSchema,
  adminPlanSpendSchema,
  type AdminPlanCronView,
  type AdminPlanDurations,
  type AdminPlanRetryResult,
  type AdminPlanRunDetail,
  type AdminPlanRunView,
  type AdminPlanSpend,
  type PlanRunRowKindName,
} from '@steado/admin-contract/plan-runs';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export const PLAN_RUNS_PAGE_SIZE = 20;

/** The cron panel is a live reading; a stale next-run time is worse than none. */
export const CRON_REFETCH_MS = 30_000;

/**
 * Which plan a read covers. Undefined is the API's `all`, which it defaults
 * to — so the three reads on this screen agree without any of them naming it.
 */
export type PlanRunKindFilter = PlanRunRowKindName | undefined;

export interface PlanRunFilters {
  readonly kind?: PlanRunKindFilter;
  readonly status?: string | undefined;
  readonly trigger?: string | undefined;
  readonly userId?: string | undefined;
  readonly weekStartDate?: string | undefined;
  /** Name, phone, or a whole run id. */
  readonly search?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface PlanRunsPage {
  readonly items: AdminPlanRunView[];
  readonly pagination: PaginationMeta;
}

const planRunsPageSchema = z.object({
  items: z.array(adminPlanRunSchema),
  pagination: paginationMetaSchema,
});

export function planRunsQueryOptions(api: ApiClient, filters: PlanRunFilters) {
  const query: Record<string, QueryValue> = {
    kind: filters.kind,
    status: filters.status,
    trigger: filters.trigger,
    userId: filters.userId,
    weekStartDate: filters.weekStartDate,
    search: filters.search,
    cursor: filters.cursor,
    limit: PLAN_RUNS_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'plan-runs', filters],
    queryFn: async (): Promise<PlanRunsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/plan-runs',
        query,
        schema: z.array(adminPlanRunSchema),
      });

      return planRunsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}

export function planRunQueryOptions(api: ApiClient, runId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'plan-run', runId],
    queryFn: (): Promise<AdminPlanRunDetail> =>
      api.request({ path: `/admin/plan-runs/${runId ?? ''}`, schema: adminPlanRunDetailSchema }),
    enabled: runId !== null,
  });
}

export function planCronQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['admin', 'plan-cron'],
    queryFn: (): Promise<AdminPlanCronView> =>
      api.request({ path: '/admin/plan-runs/cron', schema: adminPlanCronSchema }),
    refetchInterval: CRON_REFETCH_MS,
  });
}

/**
 * How long generation takes over the last `windowDays`.
 *
 * Not live like the cron panel: these are aggregates over weeks of history, so
 * a stale reading is a reading, and refetching on an interval would only add
 * load to a percentile query for no new information.
 */
export function planDurationsQueryOptions(api: ApiClient, windowDays: number, kind: PlanRunKindFilter) {
  return queryOptions({
    queryKey: ['admin', 'plan-durations', windowDays, kind ?? 'all'],
    queryFn: (): Promise<AdminPlanDurations> =>
      api.request({
        path: '/admin/plan-runs/durations',
        query: { windowDays, kind },
        schema: adminPlanDurationsSchema,
      }),
  });
}

/**
 * What generation cost over the window, by what asked for it.
 *
 * Aggregated like the durations beside it and cached the same way, for the
 * same reason: these are weeks of history, so a stale reading is still a
 * reading and an interval would only re-run a join over `LlmCall`.
 */
export function planSpendQueryOptions(api: ApiClient, windowDays: number, kind: PlanRunKindFilter) {
  return queryOptions({
    queryKey: ['admin', 'plan-spend', windowDays, kind ?? 'all'],
    queryFn: (): Promise<AdminPlanSpend> =>
      api.request({
        path: '/admin/plan-runs/spend',
        query: { windowDays, kind },
        schema: adminPlanSpendSchema,
      }),
  });
}

/**
 * Not a query. A retry re-enqueues generation and writes an audit row, so it
 * must happen exactly when an operator asks — never as a cache refill or a
 * background refetch.
 */
export function retryPlanRun(api: ApiClient, runId: string): Promise<AdminPlanRetryResult> {
  return api.request({
    path: `/admin/plan-runs/${runId}/retry`,
    method: 'POST',
    schema: adminPlanRetryResultSchema,
  });
}
